import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { constraintLevelKeys, constraintTypeKeys, constraintValueKeys } from "@/lib/query";
import type { ConstraintLevelUpdateInput } from "@/schemas/constraint-levels";
import type { ConstraintTypeUpdateInput } from "@/schemas/constraint-types";

/**
 * Staffing constraints config (EPIC-043) — what sits behind the person-profile
 * ratings: the four groupings, the NOS-backed value list, and the familiarity
 * scale.
 *
 * **The values are NOT Enable's.** They are read live from NOS through
 * `/api/constraint-values`, so there is no create, edit, reorder or delete for
 * one — only Enable's own hide overlay. The groupings keep an editable label and
 * the familiarity scale is unchanged.
 *
 * They live in one hook file because the pages are siblings and the merged
 * config page needs all of them; splitting would mean files importing each other
 * for no gain.
 */

export interface ConstraintType {
  id: string;
  /** Immutable token. Makes the seed idempotent; never editable. */
  key: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  /** People rated under this grouping — what an admin wants before retiring it. */
  ratingCount: number;
  createdAt: string;
  updatedAt: string;
}

/** One value as NOS offers it, with Enable's hide overlay applied. */
export interface ConstraintValue {
  /** The NOS guid, lowercased. Unique across all four groupings. */
  id: string;
  name: string;
  description: string | null;
  /** Hidden in Enable. Ratings against it are untouched and still count. */
  hidden: boolean;
}

export interface ConstraintGroup {
  key: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  values: ConstraintValue[];
}

export interface ConstraintValuesView {
  /** False when Enable has no NOS connection — render "not connected", not an error. */
  connected: boolean;
  groups: ConstraintGroup[];
}

export interface ConstraintLevel {
  id: string;
  ordinal: number;
  name: string;
  description: string | null;
}

async function mutate(url: string, method: string, body?: unknown) {
  const res = await fetch(url, {
    method,
    ...(body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  });
  if (!res.ok) {
    // The refusals carry a specific message (values still under a type, people
    // still rated against a constraint, a duplicate) — surfacing it is the point.
    const json = await res.json().catch(() => null);
    throw new Error(json?.error ?? "Request failed");
  }
  return res.status === 204 ? null : res.json().catch(() => null);
}

// ─── Types ────────────────────────────────────────────────────────

export function useConstraintTypes(enabled = true) {
  return useQuery({
    queryKey: constraintTypeKeys.all,
    queryFn: async (): Promise<ConstraintType[]> => {
      const res = await fetch("/api/constraint-types");
      if (!res.ok) throw new Error("Failed to fetch constraint types");
      return (await res.json()).data;
    },
    enabled,
  });
}

export function useUpdateConstraintType(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConstraintTypeUpdateInput) =>
      mutate(`/api/constraint-types/${id}`, "PUT", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: constraintTypeKeys.all });
      // A rename changes the heading the constraints are grouped under.
      queryClient.invalidateQueries({ queryKey: constraintValueKeys.all });
      toast.success("Constraint type updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useReorderConstraintType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      mutate(`/api/constraint-types/${id}/reorder`, "PATCH", { direction }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: constraintTypeKeys.all });
      queryClient.invalidateQueries({ queryKey: constraintValueKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── The pick list, read live from NOS ────────────────────────────

/**
 * Every grouping with its NOS values and Enable's hide overlay.
 *
 * Read-only apart from `useSetConstraintValueHidden`: Enable does not own these
 * values and never renames one, so a value here always matches the value in NOS.
 */
export function useConstraintValues(enabled = true) {
  return useQuery({
    queryKey: constraintValueKeys.all,
    queryFn: async (): Promise<ConstraintValuesView> => {
      const res = await fetch("/api/constraint-values");
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Failed to fetch constraints");
      }
      return (await res.json()).data;
    },
    enabled,
  });
}

/**
 * Hide or unhide a value in Enable. Never writes back to NOS, and never touches
 * a rating — a value people already hold can be hidden and they keep it.
 */
export function useSetConstraintValueHidden() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      typeKey,
      valueId,
      hidden,
    }: {
      typeKey: string;
      valueId: string;
      hidden: boolean;
    }) => mutate(`/api/constraint-values/${typeKey}/${valueId}/hidden`, "PUT", { hidden }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: constraintValueKeys.all });
      toast.success(variables.hidden ? "Constraint hidden" : "Constraint shown");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── The familiarity scale ────────────────────────────────────────

/**
 * The five rungs. Fixed in number — there is no create or delete, only a
 * rename, which never rewrites a recorded rating.
 */
export function useConstraintLevels(enabled = true) {
  return useQuery({
    queryKey: constraintLevelKeys.all,
    queryFn: async (): Promise<ConstraintLevel[]> => {
      const res = await fetch("/api/constraint-levels");
      if (!res.ok) throw new Error("Failed to fetch the constraint scale");
      return (await res.json()).data;
    },
    enabled,
  });
}

export function useUpdateConstraintLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConstraintLevelUpdateInput) =>
      mutate("/api/constraint-levels", "PUT", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: constraintLevelKeys.all });
      toast.success("Rating updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
